import { Middleware } from "redux";
import { MarketPump, Market } from "../types/Market";
import {
  requestPumpConnect,
  setPumpConnected,
  setMarkets,
  setBybitMarkets,
  updatePumps,
  updatePercentPumps,
  resetPumps,
  setKrwRate,
} from "../reducers/pumpReducer";
import { addCurrentPrice } from "../reducers/priceReducer";

let socket: WebSocket | null = null;
let reconnecting = false;
let localPumps: MarketPump[] = [];
let flushInterval: ReturnType<typeof setInterval> | null = null;
let marketsLoaded = false;

function cleanupSocket() {
  if (flushInterval) {
    clearInterval(flushInterval);
    flushInterval = null;
  }
  if (socket) {
    socket.close();
    socket = null;
  }
}

// Track which symbols we've already subscribed to current prices for
let subscribedCurrentPrices = new Set<string>();

const pumpMiddleware: Middleware = (store) => (next) => (action) => {
  if (
    !requestPumpConnect.match(action) &&
    !resetPumps.match(action)
  ) {
    return next(action);
  }

  // Let the reducer handle the action first for resetPumps
  if (resetPumps.match(action)) {
    next(action);
    // Reset localPumps to match: keep coins but zero out volumes & re-baseline prices
    localPumps = localPumps.map((p) => ({
      ...p,
      reference_price: p.latest_price,
      reference_timestamp: p.latest_timestamp,
      volume: 0,
      movement: 0,
      movementMax: 0,
    }));
    return;
  }

  if (requestPumpConnect.match(action)) {
    // If already connected and markets loaded, don't re-init
    if (socket && socket.readyState === WebSocket.OPEN && marketsLoaded) {
      return next(action);
    }

    next(action);
    initPumps(store);
    return;
  }

  return next(action);
};

async function initPumps(store: any) {
  // Fetch Upbit markets
  try {
    const marketsResponse = await fetch("https://api.upbit.com/v1/market/all", {
      method: "GET",
    });
    const marketsData = await marketsResponse.json();
    const krwMarkets: Market[] = marketsData
      .filter((m: any) => m.market.startsWith("KRW-"))
      .map((m: any) => ({
        symbol: m.market,
        name: m.english_name,
        coin: m.market.replace("KRW-", ""),
      }));
    store.dispatch(setMarkets(krwMarkets));

    // Fetch Bybit markets
    const bybitResponse = await fetch(
      "https://api.bybit.com/v5/market/instruments-info?category=linear&limit=1000",
      { method: "GET" }
    );
    const bybitData = await bybitResponse.json();
    const bybitMarketsList: string[] = bybitData.result.list.map((m: any) =>
      m.baseCoin
        .replace("1000000", "")
        .replace("100000", "")
        .replace("10000", "")
        .replace("1000", "")
        .replace("100", "")
        .replace("10", "")
    );
    store.dispatch(setBybitMarkets(bybitMarketsList));

    // Now connect WebSocket
    marketsLoaded = true;
    connectWebSocket(store, krwMarkets);
  } catch (error) {
    console.error("Error initialising pumps:", error);
    // Retry after 5s
    setTimeout(() => initPumps(store), 5000);
  }
}

function connectWebSocket(store: any, markets: Market[]) {
  cleanupSocket();
  reconnecting = false;

  socket = new WebSocket(
    "wss://d1oqt54w09hxog.cloudfront.net/websocket/v1"
  );

  socket.onopen = () => {
    console.log("Pump WebSocket connected");
    store.dispatch(setPumpConnected(true));

    socket?.send(
      '[{"ticket":"PUMP_TICKET"},{"type":"ticker","codes":[' +
        markets.map((m) => '"' + m.symbol + '"').join(",") +
        "]}]"
    );
  };

  socket.onmessage = async (e) => {
    if (e.data instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) {
          const data = JSON.parse(ev.target.result as string);
          const oldestAllowed = Date.now() - 4000;

          if (data.code && data.trade_timestamp > oldestAllowed) {
            const pumpIndex = localPumps.findIndex(
              (pump) => pump.symbol === data.code
            );

            if (pumpIndex !== -1) {
              const pump = localPumps[pumpIndex];
              pump.latest_price = data.trade_price;
              pump.latest_timestamp = data.trade_timestamp;
              pump.volume += data.trade_volume;
              pump.volume_24h = data.acc_trade_volume_24h;
              pump.movement =
                ((data.trade_price - pump.reference_price) /
                  pump.reference_price) *
                100;
              pump.movementMax = Math.max(pump.movementMax, pump.movement);
              pump.referencePrice = 0;
            } else {
              const referencePrice = data.trade_price;
              if (referencePrice) {
                // Double-check it wasn't added between the findIndex and now
                if (
                  localPumps.findIndex((p) => p.symbol === data.code) === -1
                ) {
                  localPumps.push({
                    symbol: data.code,
                    coin: data.code.replace("KRW-", ""),
                    reference_price: referencePrice,
                    latest_price: data.trade_price,
                    reference_timestamp: data.trade_timestamp,
                    latest_timestamp: data.trade_timestamp,
                    volume: data.trade_volume,
                    volume_24h: data.acc_trade_volume_24h,
                    movement: 0,
                    movementMax: 0,
                    referencePrice: 0,
                  });
                }
              }
            }
          }
        }
      };
      reader.readAsText(e.data);
    }
  };

  socket.onerror = (event) => {
    console.error("Pump WebSocket error:", event);
    store.dispatch(setPumpConnected(false));
    scheduleReconnect(store, markets);
  };

  socket.onclose = (event) => {
    console.log("Pump WebSocket closed:", event);
    store.dispatch(setPumpConnected(false));
    scheduleReconnect(store, markets);
  };

  // Flush local pump data to Redux every 500ms
  // All derivation happens here to avoid mutable-vs-frozen object issues
  flushInterval = setInterval(() => {
    if (localPumps.length === 0) return;

    const state = store.getState();
    const minVolume = state.pumps.minVolume;
    let krwRate = state.pumps.krwRate;

    // Update KRW/USDT rate from BTC
    const btcPriceArr = state.prices?.["BTC"];
    const btcPump = localPumps.find((p) => p.coin === "BTC");
    if (btcPump && btcPriceArr && btcPriceArr.length > 0) {
      const newRate = btcPump.latest_price / btcPriceArr[0].price;
      if (newRate !== krwRate && isFinite(newRate) && newRate > 0) {
        krwRate = newRate;
        store.dispatch(setKrwRate(newRate));
      }
    }

    // Clone the full pumps array for Redux (each object is a fresh copy)
    store.dispatch(updatePumps(localPumps.map((p) => ({ ...p }))));

    // Derive percentPumps
    const currentPrices: { base: string; price: number }[] =
      state.prices?.currentPrices ?? [];

    let percentPumps = localPumps
      .filter(
        (p) =>
          (p.volume * p.latest_price) / krwRate > minVolume ||
          p.coin === "BTC"
      )
      .sort((a, b) => b.movementMax - a.movementMax)
      .slice(0, 12)
      .map((p) => {
        const cp = currentPrices.find((c) => c.base === p.coin);
        return {
          ...p,
          referencePrice: cp ? cp.price : p.referencePrice,
        };
      });

    store.dispatch(updatePercentPumps(percentPumps));

    // Subscribe to price feeds for new symbols
    for (const p of percentPumps) {
      if (!subscribedCurrentPrices.has(p.coin)) {
        subscribedCurrentPrices.add(p.coin);
        store.dispatch(addCurrentPrice(p.coin));
      }
    }
  }, 500);
}

function scheduleReconnect(store: any, markets: Market[]) {
  if (!reconnecting) {
    reconnecting = true;
    socket = null;
    if (flushInterval) {
      clearInterval(flushInterval);
      flushInterval = null;
    }
    setTimeout(() => connectWebSocket(store, markets), 5000);
  }
}

export default pumpMiddleware;
